import _ from 'lodash';

import * as StringUtil from '../../utilities/stringUtil';
import {
  OIDC_SETTINGS,
  CONFERENCE_MODE,
  SESSION_KEY_ITEM,
} from '../../utilities/constants';
import { setFieldNameForGTM } from '../../utilities/gtm';

const ReportUnitCodeSessionStorageKey = 'reportUnitCode';

/**
 * sessionStorage操作用のサービスクラス
 * */
class SessionStorageService {
  /**
   * 'token'を保存する
   **/
  getToken = () => {
    return sessionStorage.getItem('token');
  };

  /**
   * 共通化フラグ
   *
   * @return {number}
   * */
  getPartnerCompanyJoiningFlag = () => {
    return Number(this.getItem('partner_company_joining_flag'));
  };

  // 検証用フラグ（active_A-284）
  getActiveA284Flag = () => {
    return Number(this.getItem('active_A284_flag'));
  };

  /**
   * token以外のget用
   * */
  getItem = key => {
    return sessionStorage.getItem(key);
  };

  /**
   * Hashのget用
   * @param {string} storageKey 取得する設定するSessionStorageキー
   * @param {string} hashKey 取得する設定するHashキー
   */
  getHashItem = (storageKey, hashKey) => {
    const hash = JSON.parse(sessionStorage.getItem(storageKey));
    if (hash) {
      return hash[hashKey];
    }
  };

  getDirectLink = () => {
    const directLink = this.getItem('directLink');
    if (directLink) {
      return JSON.parse(directLink);
    }
    return directLink;
  };

  getRelayPoint = () => {
    const relayPoint = this.getItem('relayPoint');
    if (relayPoint) {
      return JSON.parse(relayPoint);
    }
    return relayPoint;
  };
  /**
   * RelayPoint情報を設定
   * @param {string} relayPoint 設定する値
   */
  setRelayPoint = (relayPoint) => {
    this.setItem('relayPoint', JSON.stringify(relayPoint))
  };

  /**
   * tokenを設定
   *
   * @param {string} tokenString 設定する値
   * @return value
   * */
  setToken = tokenString => {
    sessionStorage.setItem('token', tokenString);
  };

  /**
   * token以外のset用
   * */
  setItem = (key, object) => {
    sessionStorage.setItem(key, object);
  };

  /**
   * Hashのset用
   * @param {string} storageKey 設定するSessionStorageキー
   * @param {string} hashKey 設定するHashキー
   * @param {string} hashValue 設定するHashキーの値
   */
  setHashItem = (storageKey, hashKey, hashValue) => {
    let hash = JSON.parse(sessionStorage.getItem(storageKey));
    if (_.isNil(hash)) {
      hash = {};
    }
    hash[hashKey] = hashValue;
    return sessionStorage.setItem(storageKey, JSON.stringify(hash));
  };

  /**
   * sessoinStorageの値を消す
   * @param {string} key 消したいキー
   */
  removeItem = key => {
    sessionStorage.removeItem(key);
  };

  /**
   * 'token'を削除
   * */
  clearToken = () => {
    sessionStorage.removeItem('token');
  };

  /**
   * oidcクライアントの情報以外のSessionStorage情報を削除
   * SessionStorage_keyに定義されたキーを全て削除します。
   */
  selectClearItem = () => {
    const crearSelectKey = [
      'token',
      'isEmpty',
      'master_menu_opening_flag',
      'function_menu_opening_flag',
      'afterSwitchUserFlag',
      'invitation_status',
      'lastNotificationLoaded',
      'strongtNotificationCount',
      'pinned_column',
      'ordered_columns',
      'init_order_columns',
      'field_tree_id',
      'field_tree_user_id',
      'job_id',
      'prime_company_id',
      'company_id',
      'company_branch_id',
      'field_id',
      'date_from',
      'date_to',
      'work_hour_zone',
      'work_start_time',
      'work_end_time',
      'work_start_time_night',
      'work_end_time_night',
      'work_start_time_midnight',
      'work_end_time_midnight',
      'working_time',
      'contract_flag',
      'use_partner_company_approval_flag',
      'view_only_flag',
      'use_roan_flg',
      'use_gk_flg',
      'use_progress_flg',
      'use_tracker_flg',
      'is_conference',
      'spot_flg',
      'use_worker_entrance_app_flg',
      'use_lw_cooperation_flag',
      'use_direct_cooperation_flag',
      'buildee_company_code',
      'companyCcusId',
      'slideClosed',
      'url',
      'WorkResulturl',
      'last_time_fetch_unapproved_works_count',
      'unapproved_works_count',
      'default_unapproved_filter_value',
    ];
    _.each(crearSelectKey, crearKey => {
      sessionStorage.removeItem(crearKey);
    });
  };

  /**
   * sessionStorageをclearする
   * */
  clear = () => {
    sessionStorage.clear();
  };

  /**
   * SP版用のCurrentPartnerCompanyDataプロパティを削除する
   */
  removeCurrentPartnerCompanyData = () => {
    sessionStorage.removeItem('currentPartnerCompanyData');
  };

  /**
   * オペレーターロールをsessionStorageから取得する（代理ログイン時のみ取得可能）
   */
  getOperatorRoleRaw = () => {
    //'oidc.user:' + 'http://localhost:5000/auth/' + ':buildee.v2.spa'
    const key = `oidc.user:${OIDC_SETTINGS.authority}:buildee.v2.spa`;
    let value = sessionStorage.getItem(key);
    if (value === null) {
      console.warn(`JWTの値がNULLです。キー名はあっていますか（key: ${key})`);
    }
    return value;
  };

  /**
   * オペレーターロールcodeを返す（代理ログイン時のみ取得可能）
   * @return "90", "91", "92", "93": 代理ログイン、false: 通常ログイン
   */
  getOperatorRoleCode = () => {
    var oidc = this.getOperatorRoleRaw();
    if (StringUtil.isNullOrEmpty(oidc)) {
      return false;
    }

    oidc = JSON.parse(oidc);
    try {
      if (
        oidc.profile.operatorrole === '90' ||
        oidc.profile.operatorrole === '91' ||
        oidc.profile.operatorrole === '92' ||
        oidc.profile.operatorrole === '93'
      ) {
        return oidc.profile.operatorrole;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  };

  /**
   * オペレーターのログインIDを返す（代理ログイン時のみ取得可能）
   * @return {string | null}
   */
  getOperatorId = () => {
    const raw = this.getOperatorRoleRaw();
    if (StringUtil.isNullOrEmpty(raw)) {
      return null;
    }
    const oidc = JSON.parse(raw);
    const operator = (oidc && oidc.profile && oidc.profile.operator) || null;
    return operator;
  };

  /**
   * 代理ログインか判定をする
   * @return true: 代理ログイン、false: 通常ログイン
   */
  isProxyLogin = () => {
    return (
      this.isExtraProxiLogin() === true || this.isInnerProxiLogin() === true
    );
  };

  /**
   * 外部ユーザーによる代理ログインか判定する
   */
  isExtraProxiLogin = () => {
    const operate = this.getOperatorRoleCode();
    if (!operate) {
      return false;
    } else {
      return operate !== '91';
    }
  };

  /**
   * 内部ユーザーによる代理ログインか判定する
   */
  isInnerProxiLogin = () => {
    const operate = this.getOperatorRoleCode();
    if (!operate) {
      return false;
    } else {
      return operate === '91';
    }
  };

  /**
   * 共通化フラグをセットする
   */
  setPartnerCompanyJoiningFlag = value => {
    sessionStorage.setItem('partner_company_joining_flag', value || 0);
  };

  // 検証用フラグ（active_A-284）をSessionStorageに設定する
  setActiveA284Flag = value => {
    sessionStorage.setItem('active_A284_flag', value || 0);
  };

  setSelectedPartnerCompanyBranchId = value => {
    sessionStorage.setItem('sessionSelectedPartnerCompanyBranchId', value);
  };

  getSelectedPartnerCompanyBranchId = () => {
    return sessionStorage.getItem('sessionSelectedPartnerCompanyBranchId');
  };

  removeSelectedPartnerCompanyBranchId = () => {
    sessionStorage.removeItem('sessionSelectedPartnerCompanyBranchId');
  };

  setSelectedPartnerCompanyBranchSafetyId = value => {
    sessionStorage.setItem('selectedPartnerCompanyBranchSafetyId', value);
  };

  getSelectedPartnerCompanyBranchSafetyId = () => {
    return sessionStorage.getItem('selectedPartnerCompanyBranchSafetyId');
  };

  removeSelectedPartnerCompanyBranchSafetyId = () => {
    sessionStorage.removeItem('selectedPartnerCompanyBranchSafetyId');
  };

  /**
   *
   * @param {string} value:
   */
  setSessionSelectedPartnerCompanyId = value => {
    sessionStorage.setItem('sessionSelectedPartnerCompanyId', value);
  };

  /**
   * 選択協力会社idをゲットする
   */
  getSessionSelectedPartnerCompanyId = () => {
    return sessionStorage.getItem('sessionSelectedPartnerCompanyId');
  };

  removeSessionSelectedPartnerCompanyId = () => {
    sessionStorage.removeItem('sessionSelectedPartnerCompanyId');
  };

  /**
   * 前回の通知一覧API取得時間をセットする
   * @param {string} value: 自社（協力会社）ID
   */
  setLastNotificationLoaded = value => {
    sessionStorage.setItem('lastNotificationLoaded', value);
  };

  /**
   * 前回の通知一覧API取得時間を取得する
   * @return {string}
   */
  getLastNotificationLoaded = () => {
    return sessionStorage.getItem('lastNotificationLoaded');
  };

  /**
   * 前回の通知一覧API取得時間削除する
   * @return {string}
   */
  removeLastNotificationLoaded = () => {
    return sessionStorage.removeItem('lastNotificationLoaded');
  };

  /**
   * 閉じるボタンを押した時に直接労務安全に戻るべきかを判定するフラグをセットする
   * @param {boolean} true: 労安側から直接現場詳細への遷移, false: 黒メニューの現場一覧からの遷移
   */

  setRoanToChoseiFlg = value => {
    sessionStorage.setItem('roanToChoseiFlg', value);
  };

  /**
   * 閉じるボタンを押した時に直接労務安全に戻るべきかを判定するフラグを取得する
   * @return {boolean} true: 労安側から直接現場詳細への遷移, false: 黒メニューの現場一覧からの遷移
   */

  getRoanToChoseiFlg = () => {
    let roanToChoseiFlg = sessionStorage.getItem('roanToChoseiFlg');
    if (_.isEmpty(roanToChoseiFlg)) {
      // 'roanToChoseiFlg'に値がない場合
      return false;
    }
    return JSON.parse(roanToChoseiFlg);
  };

  /**
   * 閉じるボタンを押した時に直接労務安全に戻るべきかを判定するフラグを削除する
   */

  removeRoanToChoseiFlg = () => {
    sessionStorage.removeItem('roanToChoseiFlg');
  };

  /**
   * 最後に期限切れチェックを行った時刻をセットする
   * @param {string} value: MomentJSで作成した日時データ
   * @description
   *  右記のような形式の文字列が入る'Mon Aug 29 2022 15:22:47 GMT+0900'
   */
  setLastCheckedTimeOfExpiration = value => {
    sessionStorage.setItem('lastCheckedTimeOfExpiration', value);
  };

  /**
   * 最後に期限切れチェックを行った時刻を取得する
   * @return {string}: MomentJSで作成した日時データ
   * @description
   *  右記のような形式の文字列'Mon Aug 29 2022 15:22:47 GMT+0900'が取り出せる
   *  Moment(${取得したデータ})としてDateオブジェクト化して利用する
   */
  getLastCheckedTimeOfExpiration = () => {
    return sessionStorage.getItem('lastCheckedTimeOfExpiration');
  };

  /**
   * 最後に期限切れチェックを行った時刻を削除する
   */
  removeLastCheckedTimeOfExpiration = () => {
    sessionStorage.removeItem('lastCheckedTimeOfExpiration');
  };

  /**
   * 重要なお知らせの数(未読の通知強)をセットする
   * @param {string} value: 自社（協力会社）ID
   */
  setStrongtNotificationCount = value => {
    sessionStorage.setItem('strongtNotificationCount', value);
  };

  /**
   * 重要なお知らせの数(未読の通知強)を取得する
   * @return {string}
   */
  getStrongtNotificationCount = () => {
    const c = sessionStorage.getItem('strongtNotificationCount');
    if (_.isNil(c)) {
      return 0;
    } else {
      return parseInt(c);
    }
  };

  /**
   * @TODO: 基本的にはこちらは使わない ( sessionSelectedPartnerCompanyIdを使って会社名を取得する )
   * @param {string} value:
   */
  setSessionSelectedPartnerCompanyName = value => {
    sessionStorage.setItem('sessionSelectedPartnerCompanyName', value);
  };

  /**
   * @TODO: 基本的にはこちらは使わない ( sessionSelectedPartnerCompanyIdを使って会社名を取得する )
   * 選択協力会社名をゲットする
   */
  // getSessionSelectedPartnerCompanyName = () => {
  getSelectedPartnerCompanyName = () => {
    return sessionStorage.getItem('sessionSelectedPartnerCompanyName');
  };

  removeSelectedPartnerCompanyName = () => {
    sessionStorage.removeItem('sessionSelectedPartnerCompanyName');
  };

  setSelectedPartnerCompanyAgencyRegistrationPermissionStatus = value => {
    sessionStorage.setItem(
      'selectedPartnerCompanyAgencyRegistrationPermissionStatus',
      value
    );
  };

  getSelectedPartnerCompanyAgencyRegistrationPermissionStatus = () => {
    return Number(
      sessionStorage.getItem(
        'selectedPartnerCompanyAgencyRegistrationPermissionStatus'
      )
    );
  };

  /**
   * 共通協力会社かどうかの判定をする
   * @return true: 共通協力会社, false: 元請け固有
   */
  isCommonPartnerCompany = () => {
    let value = sessionStorage.getItem('partner_company_joining_flag');
    try {
      value = parseInt(value);
      return value === 1;
    } catch (e) {
      console.warn(
        `元請け固有フラグの値が不正です。ロジックのチェックをしてください(${value})`
      );
      return false; // 元請け固有にして返す（共通協力会社にした方が操作によるインパクトが大きそうなので）
    }
  };

  /**
   * マスター管理Menuの開閉状態をセットする
   */
  setOpeningMasterMenu = openingFlag => {
    // 会議モードのがOFFのときのみmaster_menu_opening_flagを更新
    if (sessionStorage.getItem('is_conference') === CONFERENCE_MODE.off.key) {
      sessionStorage.setItem('master_menu_opening_flag', openingFlag ? 1 : 0);
    }
  };

  /**
   * マスター管理Menuの開閉状態を判定をする
   * マスター管理Menuを一度も閉じていない場合、sessionStorageには追加されない為、
   * openingFlagがnullであればopen状態(default)とする
   * @return true: マスターMenuが開いている, false: マスターMenuが閉じている
   */
  isOpeningMasterMenu = () => {
    let openingFlag = sessionStorage.getItem('master_menu_opening_flag');
    // 会議モードのがONであればfalseを返す

    if (
      sessionStorage.getItem('is_conference') === CONFERENCE_MODE.off.key &&
      (openingFlag === null || parseInt(openingFlag) === 1)
    ) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * 機能一覧Menuの開閉状態をセットする
   */
  setOpeningFunctionMenu = openingFlag => {
    // 会議モードのがOFFのときのみfunction_menu_opening_flagを更新
    if (sessionStorage.getItem('is_conference') === CONFERENCE_MODE.off.key) {
      sessionStorage.setItem('function_menu_opening_flag', openingFlag ? 1 : 0);
    }
  };

  /**
   * 機能一覧Menuの開閉状態を判定をする
   * 機能一覧Menuを一度も閉じていない場合、sessionStorageには追加されない為、
   * openingFlagがnullであればopen状態(default)とする
   * @return true: 機能一覧Menuが開いている, false: 機能一覧Menuが閉じている
   */
  isOpeningFunctionMenu = () => {
    let openingFlag = sessionStorage.getItem('function_menu_opening_flag');
    // 会議モードのがONであればfalseを返す
    if (
      sessionStorage.getItem('is_conference') === CONFERENCE_MODE.off.key &&
      (openingFlag === null || parseInt(openingFlag) === 1)
    ) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * 元請選択画面で選んだ自社（協力会社）のIDをセットする
   * @param {string} value: 自社（協力会社）ID
   */
  setSessionPartnerCompanyIdOfPrimeCompany = value => {
    sessionStorage.setItem('sessionPartnerCompanyIdOfPrimeCompany', value);
  };

  /**
   * 元請選択画面で選んだ自社（協力会社）のIDを取得する
   * @return {string}
   */
  getSessionPartnerCompanyIdOfPrimeCompany = () => {
    return sessionStorage.getItem('sessionPartnerCompanyIdOfPrimeCompany');
  };

  /**
   * 帳票印刷の日次月次設定を保存する
   * 月次にして日付を変更した場合に月次のままにするためsessionStorageに格納が必要
   * @param {string} unitCode: 日次(1) 月次(2)
   */
  setReportUnitCode = unitCode => {
    sessionStorage.setItem(ReportUnitCodeSessionStorageKey, unitCode);
  };

  /**
   * 帳票印刷の日次月次設定を取得する
   * @return {?number}
   */
  getReportUnitCode = () => {
    const unitCode = sessionStorage.getItem(ReportUnitCodeSessionStorageKey);
    if (!_.isNull(unitCode)) {
      return Number(unitCode);
    }

    return null;
  };

  /**
   * 帳票印刷の日次月次設定を削除する
   * @return {string}
   */
  removeReportUnitCode = () => {
    sessionStorage.removeItem(ReportUnitCodeSessionStorageKey);
  };

  /**
   * 施工体制の検索条件を保存する
   *
   * @param {number}fieldId: 現場ID
   * @param {object}param: 検索条件
   * @param {number}page: ページ数
   * */
  setFieldTreeSearchParam = (fieldId, param, page) => {
    const obj = {};
    obj[fieldId] = {
      param,
      page,
    };
    sessionStorage.setItem('fieldTreeSearchParam', JSON.stringify(obj));
  };

  /**
   * 施工体制の検索条件を取得する
   *
   * @return {object}検索条件を保持する辞書
   * */
  getFieldTreeSearchParam = () => {
    const obj = sessionStorage.getItem('fieldTreeSearchParam');
    if (obj !== null) {
      return JSON.parse(obj);
    }
    return {};
  };

  /**
   * 施工体制の検索条件を削除する
   * */
  removeFieldTreeSearchParam = () => {
    sessionStorage.removeItem('fieldTreeSearchParam');
  };

  setSelectedBatchWorkListIds = selectedWorkBatchListIds => {
    sessionStorage.setItem(
      'selectedWorkBatchListIds',
      selectedWorkBatchListIds
    );
  };

  getSelectedBatchWorkListIds = () => {
    const selectedWorkBatchListIds = sessionStorage.getItem(
      'selectedWorkBatchListIds'
    );
    if (selectedWorkBatchListIds) {
      return selectedWorkBatchListIds.split(',');
    }
    return [];
  };

  setPrivateBatchWorkListIds = privateWorkListIds => {
    sessionStorage.setItem('privateWorkBatchListIds', privateWorkListIds);
  };

  getPrivateBatchWorkListIds = () => {
    const selectedPrivateWorkBatchListIds = sessionStorage.getItem(
      'privateWorkBatchListIds'
    );
    if (selectedPrivateWorkBatchListIds) {
      return selectedPrivateWorkBatchListIds.split(',');
    }
    return [];
  };

  /**
   * CO2出力機能のフラグをセットする
   */
  setCo2ReportFlag = value => {
    sessionStorage.setItem('co2_report_flag', value ? 1 : 0);
  };

  /**
   * CO2有効の元請か判定する
   */
  getCo2ReportFlag = () => {
    return sessionStorage.getItem('co2_report_flag') === '1' ? true : false;
  };

  setIsPartnerCompanyBranchExist = isPartnerCompanyBranchExist => {
    sessionStorage.setItem(
      'partner_company_branch_exist_flag',
      isPartnerCompanyBranchExist
    );
  };

  getIsPartnerCompanyBranchExist = () => {
    return sessionStorage.getItem('partner_company_branch_exist_flag') === '1';
  };

  /**
   * ログイン直後フラグの取得
   * @param {string} key
   */
  getAfterLoginEventsFlag = () => {
    return this.getItem('afterLoginEventsFlag');
  };

  /**
   * ログイン直後フラグの設定
   * @param {string} key
   */
  setAfterLoginEventsFlag = bool => {
    if (bool) {
      this.setItem('afterLoginEventsFlag', true);
    } else {
      sessionStorage.removeItem('afterLoginEventsFlag');
    }
  };

  setAfterSwitchUser = bool => {
    sessionStorage.setItem('afterSwitchUserFlag', bool ? '1' : '0');
  };

  getAfterSwitchUser = () =>
    sessionStorage.getItem('afterSwitchUserFlag') === '1';

  setFieldTreeHasNodeInvited = bool => {
    sessionStorage.setItem('fieldTreeHasNodeInvited', bool ? '1' : '0');
  };

  getFieldTreeHasNodeInvited = () =>
    sessionStorage.getItem('fieldTreeHasNodeInvited') === '1';

  removeFieldTreeHasNodeInvited = () => {
    sessionStorage.removeItem('fieldTreeHasNodeInvited');
  };

  /**
   * Describe display of tab plan or tab result in progress service
   * @param {number} flag
   */
  setIsPlanTabFlg = flag => {
    this.setItem('is_plan_tab_flg', flag);
  };

  getIsPlanTabFlg = () => {
    return this.getItem('is_plan_tab_flg');
  };

  removeIsPlanTabFlg = () => {
    this.removeItem('is_plan_tab_flg');
  };

  /**
   * Describe mainworks or place is picked from BS001_ProgressSchedule to BS002_ProgressScheduleEdit
   * @param {number} ids
   */
  setItemIds = ids => {
    this.setItem('item_ids', ids);
  };

  getItemIds = () => {
    return this.getItem('item_ids');
  };

  removeItemIds = () => {
    this.removeItem('item_ids');
  };

  /**
   * Describe axisType is picked from BS001_ProgressSchedule to BS002_ProgressScheduleEdit
   * @param {number} id
   */
  setAxisType = id => {
    this.setItem('axis_type', id);
  };

  getAxisType = () => {
    return this.getItem('axis_type');
  };

  removeAxisType = () => {
    this.removeItem('axis_type');
  };

  getBeforePageName = () => {
    return this.getItem(SESSION_KEY_ITEM.beforePageName);
  };

  /**
   * Describe listCheckedClasses is checked in BS006_ProgressSetting of the respective construction sites
   * @param {number} fieldId
   * @param {object} params
   */
  setListCheckedClasses = params => {
    this.setItem(`listCheckedClass`, JSON.stringify(params));
  };

  getListCheckedClasses = () => {
    const params = this.getItem(`listCheckedClass`);
    if (params) {
      return JSON.parse(params);
    }
    return [];
  };

  removeListCheckedClasses = () => {
    this.removeItem('listCheckedClass');
  };

  /**
   * Describe WorkTypeSetting is returned from BS006_ProgressSetting to BS005_ProgressSettingLocation
   * @param {number} fieldId
   * @param {object} objectId
   */
  setWorkTypeSetting = objectId => {
    this.setItem('workTypeSetting', JSON.stringify(objectId));
  };

  getWorkTypeSetting = () => {
    return JSON.parse(this.getItem('workTypeSetting'));
  };

  removeWorkTypeSetting = () => {
    this.removeItem('workTypeSetting');
  };

  setPinnedColumn = pinnedColumn => {
    this.setItem('pinned_column', pinnedColumn);
  };

  getPinnedColumn = () => {
    return Number(this.getItem('pinned_column')) || 0;
  };

  setOrderColumn = orderedColumns => {
    this.setItem('ordered_columns', JSON.stringify(orderedColumns));
  };

  getOrderColumn = () => {
    return JSON.parse(this.getItem('ordered_columns'));
  };

  setInitOrderColumn = orderedColumns => {
    this.setItem('init_order_columns', JSON.stringify(orderedColumns));
  };

  getInitOrderColumn = () => {
    return JSON.parse(this.getItem('init_order_columns'));
  };

  /**
   * FieldTreeIDをnumberで取得
   *
   * @return {number} number型のfield_tree_id
   * @memberof sessionStorageService
   */
  getFieldTreeId = () => {
    let fieldTreeId = this.getItem('field_tree_id');
    if (fieldTreeId !== null) {
      fieldTreeId = Number(fieldTreeId);
    }
    return fieldTreeId;
  };

  /**
   * 現場ツリーIDをセットする
   * @param {number} fieldTreeId
   */
  setFieldTreeId = fieldTreeId => {
    sessionStorage.setItem('field_tree_id', fieldTreeId);
  };

  /**
   * 現場ツリーユーザーIDをセットする
   * @param {number} fieldTreeUserId
   */
  setFieldTreeUserId = fieldTreeUserId => {
    sessionStorage.setItem('field_tree_user_id', fieldTreeUserId);
  };

  setJobId = jobId => {
    sessionStorage.setItem('job_id', jobId);
  };

  /**
   * 会社IDを取得する
   * @return {string}
   * */
  getCompanyId = () => {
    return this.getItem('company_id');
  };

  /**
   * 支店IDを取得する
   * @return {string}
   * */
  getCompanyBranchId = () => {
    return this.getItem('company_branch_id');
  };

  /**
   * 支店IDをセットする
   * @param {number} companyBranchId 支店ID
   */
  setCompanyBranchId = companyBranchId => {
    sessionStorage.setItem('company_branch_id', companyBranchId);
  };

  /**
   * 現場IDを取得する
   * @return {string}
   * */
  getFieldId = () => {
    return this.getItem('field_id');
  };

  /**
   * 期間の場合は True 日付の場合は False
   * */
  getIsTerm = () => {
    return this.getFromDate() !== this.getToDate();
  };

  /**
   * 期間開始日を取得する
   * @TODO : return前にmomentJSを使用してformatする
   * ※ 修正箇所が多い為、2019年10月リリース後に対応する予定
   * */
  getFromDate = () => {
    return this.getItem('date_from');
  };

  /**
   * 期間終了日を取得する
   * @TODO : return前にmomentJSを使用してformatする
   * ※ 修正箇所が多い為、2019年10月リリース後に対応する予定
   * */
  getToDate = () => {
    return this.getItem('date_to');
  };

  /**
   * 作業時間帯を取得する
   * @return {string}
   * */
  getWorkHourZone = () => {
    return this.getItem('work_hour_zone');
  };

  /**
   * 作業開始時間_昼を取得する
   * @return {string}
   * */
  getWorkStartTime = () => {
    return this.getItem('work_start_time');
  };

  /**
   * 作業終了時間_昼を取得する
   * @return {string}
   * */
  getWorkEndTime = () => {
    return this.getItem('work_end_time');
  };

  /**
   * 作業開始時間_夜を取得する
   * @return {string}
   * */
  getWorkStartTimeNight = () => {
    return this.getItem('work_start_time_night');
  };

  /**
   * 作業終了時間_夜を取得する
   * @return {string}
   * */
  getWorkEndTimeNight = () => {
    return this.getItem('work_end_time_night');
  };

  /**
   * 作業開始時間_深夜を取得する
   * @return {string}
   * */
  getWorkStartTimeMidnight = () => {
    return this.getItem('work_start_time_midnight');
  };

  /**
   * 作業終了時間_深夜を取得する
   * @return {string}
   * */
  getWorkEndTimeMidnight = () => {
    return this.getItem('work_end_time_midnight');
  };

  /**
   * 契約フラグを取得する
   * @return {int}
   * */
  getContractFlag = () => {
    let contractFlag = this.getItem('contract_flag');
    if (!StringUtil.isNullOrEmpty(contractFlag)) {
      contractFlag = Number(contractFlag);
    }
    return contractFlag;
  };

  /**
   * 協力会社承認機能ご利用フラグを取得する
   * @return {number} 0: 未使用, 1: 使用
   */
  getUsePartnerCompanyApprovalFlag = () => {
    return Number(this.getItem('use_partner_company_approval_flag'));
  };

  /**
   * 協力会社承認機能ご利用フラグをセットする
   * @param {number} usePartnerCompanyApprovalFlag 協力会社承認機能ご利用フラグ
   */
  setUsePartnerCompanyApprovalFlag = usePartnerCompanyApprovalFlag => {
    sessionStorage.setItem(
      'use_partner_company_approval_flag',
      usePartnerCompanyApprovalFlag
    );
  };

  /**
   * 閲覧制限フラグを取得する
   * */
  getViewOnlyFlag = () => {
    return this.getItem('view_only_flag') === 'true' ? true : false;
  };

  /**
   * 閲覧制限フラグをセットする
   * @param {boolean} viewOnlyFlag
   * */
  setViewOnlyFlag = viewOnlyFlag => {
    sessionStorage.setItem('view_only_flag', viewOnlyFlag);
  };

  /**
   * 閲覧制限フラグを削除する
   */
  removeViewOnlyFlag = () => {
    sessionStorage.removeItem('view_only_flag');
  };

  removeInformationRelatedToField = () => {
    const informationRelatedToField = [
      'field_id',
      'date_from',
      'date_to',
      'work_hour_zone',
      'current_field', // SP版用プロパティ
    ];
    _.each(informationRelatedToField, key => this.removeItem(key));
    setFieldNameForGTM('');
  };

  /**
   * SP版用のsessionStorageプロパティを削除する
   */
  removeCurrentField = () => {
    sessionStorage.removeItem('current_field');
  };

  /**
   * 労務安全利用フラグ取得
   * @param {string} key
   */
  getUseRoanFlag = () => {
    return this.getItem('use_roan_flg');
  };

  getUseGkFlag = () => {
    return this.getItem('use_gk_flg');
  };

  getUseProgressFlag = () => {
    return this.getItem('use_progress_flg');
  };

  getUseTrackerFlag = () => {
    return this.getItem('use_tracker_flg');
  }

  isConferenceMode = () => {
    return this.getItem('is_conference') === CONFERENCE_MODE.on.key;
  };

  getSpotFlg() {
    const spotFlg = JSON.parse(this.getItem('spot_flg'));
    return spotFlg ? spotFlg : false;
  }

  setSpotFlg(spotFlg) {
    this.setItem('spot_flg', spotFlg);
  }

  setUseWorkerEntranceAppFlg(useWorkerEntranceAppFlg) {
    this.setItem('use_worker_entrance_app_flg', useWorkerEntranceAppFlg);
  }

  getUseWorkerEntranceAppFlg() {
    const useWorkerEntranceAppFlg = JSON.parse(this.getItem('use_worker_entrance_app_flg'));
    return useWorkerEntranceAppFlg ? useWorkerEntranceAppFlg : false;
  }

  /**
   * LINE WORKS連携ご利用フラグを取得する
   * @return {boolean} false: 未使用, true: 使用
   */
  getUseLwCooperationFlag = () => {
    return this.getItem('use_lw_cooperation_flag') === 'true';
  };

  /**
   * LINE WORKS連携ご利用フラグをセットする
   * @param {boolean} useLwCooperationFlag 外部サービス連携ご利用フラグ
   */
  setUseLwCooperationFlag = useLwCooperationFlag => {
    sessionStorage.setItem('use_lw_cooperation_flag', useLwCooperationFlag);
  };

  /**
   * direct連携ご利用フラグを取得する
   * @return {boolean} false: 未使用, true: 使用
   */
  getUseDirectCooperationFlag = () => {
    return this.getItem('use_direct_cooperation_flag') === 'true';
  };

  /**
   * direct連携ご利用フラグをセットする
   * @param {boolean} useDIrectCooperationFlag 外部サービス連携ご利用フラグ
   */
  setUseDirectCooperationFlag = useDirectCooperationFlag => {
    sessionStorage.setItem(
      'use_direct_cooperation_flag',
      useDirectCooperationFlag
    );
  };

  /**
   * directのOauth情報を取得する
   * @return {object} directのOauth情報
   */
  getDirectOauthInfo = () => {
    return this.getItem('directOauthInfo');
  };

  /**
   * directのOauth情報をセットする
   * @param {object} directOauthInfo direct認証情報
   */
  setDirectOauthInfo = directOauthInfo => {
    this.setItem('directOauthInfo', JSON.stringify(directOauthInfo))
  };

  /**
   * directのOauth情報を削除する
   */
  removeDirectOauthInfo = () => {
    this.removeItem('directOauthInfo')
  };

  isKajimaCompany = () => {
    return Number(this.getItem('buildee_company_code')) === 1;
  };

  setLastTimeFetchUnapprovedWorksCount = value => {
    sessionStorage.setItem('last_time_fetch_unapproved_works_count', value);
  };

  getLastTimeFetchUnapprovedWorksCount = () => {
    return sessionStorage.getItem('last_time_fetch_unapproved_works_count');
  };

  removeLastTimeFetchUnapprovedWorksCount = () => {
    sessionStorage.removeItem('last_time_fetch_unapproved_works_count');
  };

  setUnapprovedWorksCount = value => {
    sessionStorage.setItem('unapproved_works_count', JSON.stringify(value));
  };

  getUnapprovedWorksCount = () => {
    return JSON.parse(sessionStorage.getItem('unapproved_works_count'));
  };

  removeUnapprovedWorksCount = () => {
    sessionStorage.removeItem('unapproved_works_count');
  };

  setDefaultUnapprovedFilterValue = (defaultValue) => {
    this.setItem('default_unapproved_filter_value', defaultValue)
  };

  getDefaultUnapprovedFilterValue = () => {
    return JSON.parse(this.getItem('default_unapproved_filter_value'));
  };
}

export default new SessionStorageService();



// WEBPACK FOOTER //
// ./src/services/utilities/SessionStorageService.js